/** The attribute selector for a step's target id. */
export function guideTargetSelector(target: string): string {
  return `[data-guide-target="${CSS.escape(target)}"]`;
}

/** Resolves a step's `data-guide-target` id to its element, or `null` when nothing carries it. */
export function resolveGuideTarget(target: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(guideTargetSelector(target));
}

function hasBox(element: Element | null | undefined): boolean {
  const rect = element?.getBoundingClientRect();

  return !!rect && rect.width > 0 && rect.height > 0;
}

/**
 * The element the highlight should actually cut around.
 *
 * The spotlight is a box-shadow spread around the target's own box, so a target with a zero-height
 * rect — a wrapper whose children are floated, absolutely positioned, or not yet laid out — dims
 * the page, cuts out nothing, and drags its whole subtree above the overlay via
 * `.introjs-showElement`. Falling back to the nearest ancestor that has a box keeps the step
 * usable instead of silently breaking the tour.
 */
export function resolveGuideTargetBox(target: string): HTMLElement | null {
  const element = resolveGuideTarget(target);

  if (!element) {
    return null;
  }

  let candidate: HTMLElement | null = element;
  while (candidate && !hasBox(candidate) && candidate !== document.body) {
    candidate = candidate.parentElement;
  }

  return hasBox(candidate) ? candidate : null;
}

/** Whether the step can be highlighted at all — directly or via an ancestor with a box. */
export function isGuideTargetMeasurable(target: string): boolean {
  return !!resolveGuideTargetBox(target);
}

/** Resolves once the target has a measurable box, or gives up after `timeoutMs`. */
export function waitForGuideTarget(target: string | undefined, timeoutMs = 3000): Promise<void> {
  if (!target || isGuideTargetMeasurable(target)) {
    return Promise.resolve();
  }

  return new Promise((done) => {
    const startedAt = Date.now();
    const tick = () => {
      if (isGuideTargetMeasurable(target) || Date.now() - startedAt > timeoutMs) {
        done();
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    tick();
  });
}

const POLL_MS = 50;
